import { reportCustomEvent } from '@/utils/arms';
import { truncateArmsFailDetail } from '@/utils/arms/common';

export const ARMS_LOGIN_EVENT_TYPE = 'login' as const;
export const ARMS_LOGIN_EVENT_GROUP = 'auth' as const;

const LOGIN_EVENT_NAME = {
    ATTEMPT: 'login_attempt',
    SUCCESS: 'login_success',
    FAIL: 'login_fail',
} as const;

/**
 * 登录失败原因（按场景细分，供 Grafana 告警与下钻）
 */
export type TLoginArmsFailReason =
    /** 游客 device_id 未就绪 */
    | 'device_id_missing'
    /** /auth/login/v1 业务失败或异常 */
    | 'auth_login_api_failed'
    /** Google：signInWithRedirect 发起失败（登录页/弹窗） */
    | 'google_sign_in_redirect_failed'
    /** Google：回站后 getRedirectResult / getIdToken 等失败 */
    | 'google_redirect_result_failed'
    /** Facebook：App ID 未配置，无法发起 OAuth */
    | 'facebook_app_id_not_configured'
    /** Facebook：移动端整页 OAuth 发起失败（跳转前） */
    | 'facebook_oauth_redirect_failed'
    /** Facebook：OAuth 回跳 hash 带 error（非用户取消） */
    | 'facebook_oauth_hash_error'
    /** Facebook：OAuth state 校验失败 */
    | 'facebook_oauth_invalid_state'
    /** Facebook：access_token 换 Firebase 凭证 / idToken 失败 */
    | 'facebook_firebase_credential_failed'
    /** Facebook：桌面 SDK 登录失败（非用户取消） */
    | 'facebook_sdk_login_failed';

export interface ILoginArmsTrackingProps {
    login_method: string;
    trigger_scene: string;
}

export interface IReportLoginFailProps extends ILoginArmsTrackingProps {
    fail_reason: TLoginArmsFailReason;
    /** 错误摘要（如 Error.message、businessCode），最长 200 字符 */
    fail_detail?: string;
}

function toArmsProperties(props: Record<string, string | number>): Record<string, string | number> {
    return {
        ...props,
        trigger_scene: props.trigger_scene || '',
    };
}

function sendLoginArmsEvent(name: string, value: number, properties: Record<string, string | number>): void {
    reportCustomEvent(name, ARMS_LOGIN_EVENT_TYPE, {
        group: ARMS_LOGIN_EVENT_GROUP,
        value,
        properties: toArmsProperties(properties),
    });
}

/** 用户发起一次登录尝试（成功率分母） */
export function reportLoginAttempt(props: ILoginArmsTrackingProps): void {
    sendLoginArmsEvent(LOGIN_EVENT_NAME.ATTEMPT, 1, {
        login_method: props.login_method,
        trigger_scene: props.trigger_scene,
    });
}

/** 业务登录成功（/auth/login/v1 成功） */
export function reportLoginSuccess(props: ILoginArmsTrackingProps & { login_type: string }): void {
    sendLoginArmsEvent(LOGIN_EVENT_NAME.SUCCESS, 1, {
        login_method: props.login_method,
        trigger_scene: props.trigger_scene,
        result_status: 'success',
        login_type: props.login_type,
    });
}

/** 登录失败（用户取消不上报） */
export function reportLoginFail(props: IReportLoginFailProps): void {
    sendLoginArmsEvent(LOGIN_EVENT_NAME.FAIL, 0, {
        login_method: props.login_method,
        trigger_scene: props.trigger_scene,
        result_status: 'fail',
        fail_reason: props.fail_reason,
        ...(props.fail_detail ? { fail_detail: truncateArmsFailDetail(props.fail_detail) } : {}),
    });
}
